import { useEffect, useState } from 'react';
import type { RemoteAccess } from '@supporchat/shared';
import { api } from '../lib/api';
import { openExternalApp } from '../lib/protocol';

/**
 * Acesso remoto ao computador do cliente, na conversa.
 *
 * O ID fica guardado por cliente para o atendente não precisar procurá-lo em
 * conversa antiga nem pedir de novo a quem está justamente sem conseguir usar o
 * computador. *Abrir RustDesk* entrega o esquema `rustdesk://` ao sistema por um
 * iframe descartável (ver `lib/protocol`), que não navega o painel nem abre aba —
 * as duas tentativas anteriores faziam o atendimento sair da tela ou uma aba
 * piscar. Depende do app instalado, então *Copiar ID* está sempre ao lado.
 *
 * A senha não vem junto com o resto: ela é pedida em uma chamada separada, que
 * fica registrada no log do servidor.
 */
export function RemoteAccessPanel({ conversationId }: { conversationId: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<RemoteAccess | null>(null);
  const [rustdeskId, setRustdeskId] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    if (!open) return;

    let ativo = true;
    void api
      .getRemoteAccess(conversationId)
      .then((remote) => {
        if (!ativo) return;
        setData(remote);
        setRustdeskId(remote.rustdeskId ?? '');
        setNote(remote.note ?? '');
      })
      .catch((err: unknown) => ativo && setError((err as Error).message));

    return () => {
      ativo = false;
    };
  }, [open, conversationId]);

  // Trocar de conversa não pode deixar a senha do cliente anterior na tela.
  useEffect(() => {
    setOpen(false);
    setRevealed(null);
    setPassword('');
    setData(null);
    setLaunched(false);
  }, [conversationId]);

  const copy = async (texto: string, oque: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopied(oque);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('O navegador não permitiu copiar. Selecione e copie à mão.');
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await api.saveRemoteAccess(conversationId, {
        rustdeskId,
        note,
        // String vazia apaga a senha guardada; `undefined` mantém.
        ...(password ? { password } : {}),
      });
      setData(updated);
      setPassword('');
      setRevealed(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const reveal = async () => {
    setError(null);
    try {
      const { password: senha } = await api.revealRemoteAccessPassword(conversationId);
      setRevealed(senha);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="contact-editor">
      <button
        className="btn btn--sm"
        title="Acesso remoto ao computador do cliente"
        onClick={() => setOpen((current) => !current)}
      >
        🖥 Acesso remoto
      </button>

      {open && (
        <form className="contact-editor__panel" onSubmit={(event) => void save(event)}>
          <div className="field">
            <label htmlFor="remote-id">ID do RustDesk</label>
            <input
              id="remote-id"
              value={rustdeskId}
              placeholder="123 456 789"
              inputMode="numeric"
              onChange={(event) => setRustdeskId(event.target.value)}
            />
          </div>

          {data?.rustdeskId && (
            <>
              <div className="contact-editor__actions">
                <button
                  className="btn btn--sm btn--primary"
                  type="button"
                  onClick={() => {
                    if (!data.connectUri) return;
                    openExternalApp(data.connectUri);
                    setLaunched(true);
                  }}
                >
                  Abrir RustDesk
                </button>
                <button
                  className="btn btn--sm"
                  type="button"
                  onClick={() => void copy(data.rustdeskId!, 'id')}
                >
                  {copied === 'id' ? 'Copiado' : 'Copiar ID'}
                </button>
              </div>

              {/*
               * O lançamento não tem retorno: a página não fica sabendo se o
               * RustDesk abriu. Em vez de fingir sucesso, o caminho manual fica à
               * mão para quem não tem o app registrado no sistema.
               */}
              {launched && (
                <span className="field__hint">
                  Se o RustDesk não abriu, ele pode não estar instalado ou registrado neste computador —
                  use <strong>Copiar ID</strong> e cole no app.
                </span>
              )}
            </>
          )}

          <div className="field">
            <label htmlFor="remote-password">Senha fixa (opcional)</label>
            <input
              id="remote-password"
              type="password"
              autoComplete="off"
              value={password}
              placeholder={data?.hasPassword ? '•••••••• (guardada)' : 'sem senha guardada'}
              disabled={data ? !data.canStorePassword : false}
              onChange={(event) => setPassword(event.target.value)}
            />
            <span className="field__hint">
              {data && !data.canStorePassword
                ? 'O servidor está sem chave de cifra (REMOTE_ACCESS_SECRET); só o ID pode ser guardado.'
                : 'Guardada cifrada. Deixe em branco para manter a atual; salve vazia para apagar.'}
            </span>
          </div>

          {data?.hasPassword && (
            <div className="contact-editor__actions">
              {revealed ? (
                <>
                  <code className="remote-password">{revealed}</code>
                  <button
                    className="btn btn--sm"
                    type="button"
                    onClick={() => void copy(revealed, 'senha')}
                  >
                    {copied === 'senha' ? 'Copiada' : 'Copiar'}
                  </button>
                  <button className="btn btn--sm" type="button" onClick={() => setRevealed(null)}>
                    Ocultar
                  </button>
                </>
              ) : (
                <button className="btn btn--sm" type="button" onClick={() => void reveal()}>
                  Ver senha
                </button>
              )}
            </div>
          )}

          <div className="field">
            <label htmlFor="remote-note">Observação</label>
            <input
              id="remote-note"
              value={note}
              placeholder="PC do simulador 2, Windows 11"
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          {error && <div className="alert alert--error">{error}</div>}

          <div className="contact-editor__actions">
            <button className="btn btn--sm btn--primary" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button className="btn btn--sm" type="button" onClick={() => setOpen(false)}>
              Fechar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
